import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { AncillaryWorkflowPage } from 'src/shared/models/workflow-page-ancllary.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { PageInterface } from 'src/app/workflow/planner/page/page.interface';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { TotalUosByBlock } from 'src/app/workflow/base-classes/total-uos-by-block.component';
import { TotalUOSShiftRecordService } from 'src/shared/services/shift-record-total-uos.service';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';


@Component({
    selector: 'smt-enter-actual-uos',
    templateUrl: './enter-actual-uos.component.html',
    styleUrls: ['./enter-actual-uos.component.scss']
})
export class TotalUosEnterActualComponent extends TotalUosByBlock implements OnInit, PageInterface {
    @Input() page: AncillaryWorkflowPage;
    @Output() previousRequested = new EventEmitter();

    private enterUOSActualForm: UntypedFormGroup = new UntypedFormGroup({});
    public formGroups: UntypedFormGroup[];
    loading: boolean;
    
    constructor(
        protected _router: Router, 
        protected _shiftBlockService: ShiftBlockService,
        protected _totalUosShiftRecordService: TotalUOSShiftRecordService, 
        protected _workflowUtilityService: WorkflowUtilityService) {             
        super(_router, _shiftBlockService, _totalUosShiftRecordService, _workflowUtilityService);        
    }

    public async ngOnInit() {          
        this.formGroups = [ this.enterUOSActualForm ];

        super.initialize(this.page, () => this.initializeForm());
    } 
    
    private async initializeForm() {
        super.initializeShiftRecords(true).then(() => {
                // Doing this will trigger the form's changes subscription which will force a form validation.
                // This is necessary because the child components of this form are not triggering the proper
                // form change subscription once the data is all loaded.
                this.enterUOSActualForm.updateValueAndValidity();
            }            
        );
    }

    public isFormValid(): boolean { 
        let invalidShiftBlocks: number[] = [];  
        let shiftBlocksWithValues: number[] = [];
        let shiftBlocksWithoutValues: number[] = [];
        
        // Iterate through all the 'Actual' controls.
        Object.keys(this.enterUOSActualForm.controls).filter(c => c.startsWith('actual')).forEach(controlName => {                   
            let controlValue = this.enterUOSActualForm.controls[controlName].value;
            let shiftBlockId = +controlName.split('-')[4]; 

            // If the control contains a valid number then store that shift block id.
            if (MiscUtil.isValidNonNegativeNumber(controlValue) && !shiftBlocksWithValues.some(n => n == shiftBlockId)) {
                shiftBlocksWithValues.push(shiftBlockId);
            }
            // If the control does not contain a valid number then store that shift block id.
            else if (!MiscUtil.isValidNonNegativeNumber(controlValue) && !shiftBlocksWithoutValues.some(n => n == shiftBlockId)) {
                shiftBlocksWithoutValues.push(shiftBlockId);
            }
        });

        // Loop through all the shift blocks that have values entered and check to see if any of the values in that shift block are missing.
        shiftBlocksWithoutValues.filter(n => shiftBlocksWithValues.some(s => s == n) && !invalidShiftBlocks.some(b => b == n)).forEach(shiftBlockId => {
            invalidShiftBlocks.push(shiftBlockId);
        });

        // Loop through all the shift blocks that don't have values entered and check to see if any of the values in that shift block have been entered.
        shiftBlocksWithValues.filter(n => shiftBlocksWithoutValues.some(s => s == n) && !invalidShiftBlocks.some(b => b == n)).forEach(shiftBlockId => {
            invalidShiftBlocks.push(shiftBlockId);            
        });

        return !this.loadingRecords && (!this.hasShiftBlocks() || (MiscUtil.checkArray(shiftBlocksWithValues) && !MiscUtil.checkArray(invalidShiftBlocks)));
    }

    public async save(): Promise<number> {
        this.setStatus();

        let shiftRecordsToSave = this.shiftRecords.filter(s => MiscUtil.checkArray(s.staffingPlans) && s.staffingPlans.some(s => MiscUtil.isValidNonNegativeNumber(s.amount)));

        if (MiscUtil.checkArray(shiftRecordsToSave) && shiftRecordsToSave.length > 0) {
            let ids = await this._totalUosShiftRecordService.setShiftRecordsWithAssociated(shiftRecordsToSave);
            let id = await this._totalUosShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);

            // Validate the ids returned.  If they aren't what was expected then return null which will trigger an error and prevent
            // the user from proceeding to the next work flow page.
            if (!MiscUtil.checkArray(ids) || ids.length != shiftRecordsToSave.length || !MiscUtil.isValidNumber(id)) {
                return null;
            }
        }
        
        return this.shiftRecord.id;
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.ReviewPreviousActuals;
        }
    } 

    public shouldSkipSave(): boolean {
        return false;
    }  
}
